import {
  ITochiChicken,
  ITochiRGBGradientMaskMaterial,
} from "@snackpass/snackpass-types";
import TochiMaterial from "./TochiMaterial";
import { RGBGradientMaskShader } from "src/utils/Tochi/TochiShaders";
import {
  getColorPos,
  MAX_COLORS,
  MIN_COLORS,
  padColorPosArray,
  padStringArray,
  stringArrToColor,
  stringToColor,
} from "./utils";
import { ShaderMaterial, TextureLoader, UniformsUtils } from "three";

export default class RGBGradientMaskMaterial implements TochiMaterial {
  material?: ShaderMaterial;
  maxlen: number;

  rgbMask: string;
  gradientMask: string;
  rcolors: string[];
  gcolors: string[];
  bcolors: string[];
  rcolorPos: number[];
  gcolorPos: number[];
  bcolorPos: number[];
  decalMask?: string;
  decalColor?: string;

  constructor(props: ITochiRGBGradientMaskMaterial) {
    this.rgbMask = props.rgbMask;
    this.gradientMask = props.gradientMask;
    if (
      props.rcolors.length < MIN_COLORS ||
      props.gcolors.length < MIN_COLORS ||
      props.bcolors.length < MIN_COLORS
    ) {
      throw new Error(`Each mask must have at least ${MIN_COLORS} colors`);
    }
    this.rcolors = padStringArray(props.rcolors);
    this.gcolors = padStringArray(props.gcolors);
    this.bcolors = padStringArray(props.bcolors);
    this.rcolorPos = getColorPos(this.rcolors, props.rcolorPos);
    this.gcolorPos = getColorPos(this.gcolors, props.gcolorPos);
    this.bcolorPos = getColorPos(this.bcolors, props.bcolorPos);
    this.maxlen = Math.max(
      this.rcolors.length,
      this.gcolors.length,
      this.bcolors.length
    );
    if (this.maxlen !== MAX_COLORS) {
      throw new Error(`Expected gradient mask to have ${MAX_COLORS} colors`);
    }
    if (props.decalMask && props.decalColor) {
      this.decalMask = props.decalMask;
      this.decalColor = props.decalColor;
    }
  }

  load = async () => {
    const shader = RGBGradientMaskShader(
      this.maxlen,
      this.rcolors.length,
      this.gcolors.length,
      this.bcolors.length,
      !(this.decalMask == null && this.decalColor == null)
    );
    const u = UniformsUtils.clone(shader.uniforms);
    const vs = shader.vertexShader;
    const fs = shader.fragmentShader;

    const loader = new TextureLoader();
    u.rgbMask.value = await loader.load(this.rgbMask);
    u.gradientMask.value = await loader.load(this.gradientMask);
    if (this.decalMask && this.decalColor) {
      u.decalMask.value = await loader.load(this.decalMask);
      u.decalColor.value = stringToColor(this.decalColor);
    }
    u.rcolors.value = stringArrToColor(this.rcolors);
    u.gcolors.value = stringArrToColor(this.gcolors);
    u.bcolors.value = stringArrToColor(this.bcolors);
    u.rcolorPos.value = padColorPosArray(this.rcolorPos, MAX_COLORS);
    u.gcolorPos.value = padColorPosArray(this.gcolorPos, MAX_COLORS);
    u.bcolorPos.value = padColorPosArray(this.bcolorPos, MAX_COLORS);
    u.fresnelColor.value = stringToColor("white");

    const mat = new ShaderMaterial({
      uniforms: u,
      vertexShader: vs,
      fragmentShader: fs,
    } as any);
    this.material = mat;
  };

  get = async () => {
    if (!this.material) {
      await this.load();
    }
    return this.material!;
  };

  setChicken = async (chicken: ITochiChicken) => {
    if (!this.material) return;
    this.material.uniforms.rcolors.value = stringArrToColor(
      this.rcolors,
      chicken
    );
    this.material.uniforms.gcolors.value = stringArrToColor(
      this.gcolors,
      chicken
    );
    this.material.uniforms.bcolors.value = stringArrToColor(
      this.bcolors,
      chicken
    );
    if (this.decalColor) {
      this.material.uniforms.decalColor.value = stringToColor(
        this.decalColor,
        chicken
      );
    }
    this.material.needsUpdate = true;
  };
}
